import clone from 'lodash/clone'
import get from 'lodash/get'
import slice from 'lodash/slice'
import uniqueId from 'lodash/uniqueId'
import { BLOCKS } from '@contentful/rich-text-types'
import { documentToHtmlString } from '@contentful/rich-text-html-renderer'

/**
 * extract the content type from a contentful link object
 * @param {object} section
 */
const getContentType = (section) => {
  // console.warn('[contentful-helper] getContentType', section)
  return (
    get(section, 'type') ||
    get(section, 'sys.contentType.sys.id') ||
    get(section, 'contentType', '').replace('Content', '')
  )
}

/**
 * parse contentful media link object
 * @param {object} img
 */
const getImgSrc = (img) => {
  let result = {}
  if (img) {
    const { originalSrc, fields } = img
    if (originalSrc) {
      // carryover from nacelle logic
      result = {
        src: originalSrc.startsWith('//') ? `https:${originalSrc}` : originalSrc,
        alt: 'Contentful image'
      }
    } else {
      const { file, title, altText } = fields
      const source = get(file, 'url')
      result = {
        src: source.startsWith('//') ? `https:${source}` : source,
        title,
        alt: altText || title || 'Contentful image'
      }
    }
  }
  return result
}

const getVideoSrc = (video) => {
  // console.log('card.video', video)
  let result = {}
  if (video) {
    result.poster = get(video, 'fields.poster.fields.file.url')
    result.src = get(video, 'fields.video.fields.file.url')
    result.title = (
      get(video, 'fields.video.fields.title') ||
      get(video, 'fields.entryTitle')
    )
    result.type = get(video, 'fields.video.fields.file.contentType')
    result.youtube = get(video, 'fields.youtube')
    if (get(video, 'fields.video.type') === 'NacelleReference') {
      console.warn('NacelleReference', video.fields.video)
    }
  }
  return result
}

export const contentToHtml = (content, ctx = {}) => {
  let result = ''
  if (ctx && ctx.customContentToHtml) {
    console.warn('[contentful] using cusom contentToHtmlFn')
    result = ctx.contentToHtmlFn.call(null, content)
  } else {
    const options = {
      renderNode: {
        [BLOCKS.EMBEDED_ASSET](node) {
          const url = get(node, 'data.target.fields.file.url')
          const altText = get(node, 'data.target.fields.title')
          // console.log('contentToHtml', JSON.stringify({
          //   target: get(node, 'data.target')
          // }))
          return `
            <img
              class="c-contentful-rich-text__image"
              src="${url}"
              alt="${altText}"
            />
          `
        }
      }
    }
    result = documentToHtmlString(content, options).replace(/<p>/g,'<p class="t-paragraph">')
    result = result.replace(/(?:\r\n|\r|\n)/g, '<br>');
    return result
  }
  return result
}

const mapByFieldCase = (vm, section, data) => {
  let entries = []
  try {
    entries = Object.entries(clone(section.fields))
  } catch (err) {
    throw new Error('Cannot create entries', { cause: err })
  }
  for (let [key, value] of entries) {
    // store the value on the result object to start, then handle the data
    // conversion cases. if none apply, the raw value will be left.
    data[key] = value
    switch (key) {
    case 'mobileFullHeight':
    case 'reverseDesktop':
    case 'reverseMobile':
      data[key] = `${value}` == 'true'
      break
    case 'ctaUrl':
      /* FIXME the component should be handling this
      data.ctaHandler = () => {
        vm.$router.push(value)
      }
      */
      break
    case 'columns':
      data[key] = parseInt(value, 10) || 4
      break
    case 'image':
      data.image = getImgSrc(value)
      break
    case 'tabIconImage':
      data.tabIconImage = getImgSrc(value)
      break
    case 'mobileImage':
      data.mobileImage = getImgSrc(value)
      break
    case 'featuredMedia':
      data.imageUrl = getImgSrc(value)
      break
    case 'desktopBackgroundImage':
      data.desktopBackgroundImage = getImgSrc(value)
      break
    case 'mobileBackgroundImage':
      data.mobileBackgroundImage = getImgSrc(value)
      break
    case 'backgroundImage':
      data.backgroundImage = getImgSrc(value)
      break
    case 'middleImage':
      data.middleImage = getImgSrc(value)
      break
    case 'partnerLogo':
      data.partnerLogo = getImgSrc(value)
      break
    case 'elementId':
      data.elementId = value
      break
    case 'legalcopyrightText':
    case 'text':
    case 'content':
      data.html = contentToHtml(value, {
        contentToHtmlFn: vm.contentToHtmlFn
      })
      delete data.text
      break
    case 'column':
      data[key] = []
      for (let col of value) {
        data[key].push(mapSection(vm, col))
      }
      break
    case 'socialMedia':
      data[key] = {}
      for (let [fieldKey, fieldValue] of Object.entries(value.fields)) {
        switch (fieldKey) {
        case 'title':
          data[key][fieldKey] = data[key]['title'] = fieldValue
          break
        case 'body':
          data[key][fieldKey] = data[key]['body'] = contentToHtml(fieldValue, {
            contentToHtmlFn: vm.contentToHtmlFn
          })
          break
        default:
          data[key][fieldKey] = fieldValue
        }
      }
      break
    case 'navs':
      data[key] = []
      for (let item of value) {
        for (let [itemKey, itemValue] of Object.entries(item.fields)) {
          switch (itemKey) {
          case 'entryTitle':
            item[itemKey] = item['entryTitle'] = itemValue;
            break
          case 'title':
            item[itemKey] = item['title'] = itemValue;
            break
          case 'navItems':
            item[itemKey] = [];
            for (let navItem of itemValue) {
              for (let [navItemKey, navItemValue] of Object.entries(navItem.fields)) {
                switch (navItemKey) {
                case 'description':
                  navItem[navItemKey] = navItem['description'] = navItemValue;
                  break
                case 'entryTitle':
                  navItem[navItemKey] = navItem['entryTitle'] = navItemValue;
                  break
                case 'featured':
                  navItem[navItemKey] = navItem['featured'] = navItemValue;
                  break
                case 'new':
                  navItem[navItemKey] = navItem['new'] = navItemValue;
                  break
                case 'image':
                  navItem[navItemKey] = navItem['image'] = getImgSrc(navItemValue)
                  break
                case 'linkText':
                  navItem[navItemKey] = navItem['linkText'] = navItemValue;
                  break
                case 'linkUrl':
                  navItem[navItemKey] = navItem['linkUrl'] = navItemValue;
                  break
                case 'subtitle':
                  navItem[navItemKey] = navItem['subtitle'] = navItemValue;
                  break
                case 'title':
                  navItem[navItemKey] = navItem['title'] = navItemValue;
                  break
                default:
                  navItem[navItemKey] = navItemValue;
                }
              }
              item[itemKey].push(navItem);
            }
            break
          default:
            item[itemKey] = itemValue;
          }
        }
        data[key].push(item);
      }
      break
    case 'cards':
      data[key] = []
      for (let item of value) {
        for (let [itemKey, itemValue] of Object.entries(item.fields)) {
          switch (itemKey) {
          case 'entryTitle':
            item[itemKey] = item['entryTitle'] = itemValue;
            break
          case 'image':
            item[itemKey] = getImgSrc(itemValue)
            break
          case 'mobileImage':
            item[itemKey] = getImgSrc(itemValue)
            break
          case 'video':
            item[itemKey] = getVideoSrc(itemValue)
            break
          case 'description':
            item[itemKey] = item['description'] = contentToHtml(itemValue, {
              contentToHtmlFn: vm.contentToHtmlFn
            })
            break
          case 'belowButtonText':
            item[itemKey] = item['belowButtonText'] = contentToHtml(itemValue, {
              contentToHtmlFn: vm.contentToHtmlFn
            })
            break
          case 'title':
            item[itemKey] = item['title'] = itemValue
            break
          case 'directUrl':
            item[itemKey] = item['directUrl'] = itemValue
            break
          case 'productAddToCartEnabled':
            item[itemKey] = item['productAddToCartEnabled'] = itemValue
            break
          case 'imageMarginBottom':
            item[itemKey] = item['imageMarginBottom'] = itemValue
            break
          case 'backgroundImage':
            item[itemKey] = getImgSrc(itemValue)
            break
          case 'cta':
          case 'mobileCta':
            item[itemKey] = {}
            if (itemValue.fields) {
              for (let [ctaKey, ctaValue] of Object.entries(itemValue.fields)) {
                switch (ctaKey) {
                case 'buttonSize':
                  item[itemKey][ctaKey] = item[itemKey]['buttonSize'] = ctaValue
                  break
                case 'entryTitle':
                  item[itemKey][ctaKey] = item[itemKey]['entryTitle'] = ctaValue
                  break
                case 'text':
                  item[itemKey][ctaKey] = item[itemKey]['text'] = ctaValue
                  break
                case 'type':
                  item[itemKey][ctaKey] = item[itemKey]['type'] = ctaValue
                  break
                case 'url':
                  item[itemKey][ctaKey] = item[itemKey]['url'] = ctaValue
                  break
                default:
                  item[itemKey][ctaKey] = ctaValue
                }
              }
            }
            break
          default:
            item[itemKey] = itemValue
          }
        }
        data[key].push(item);
      }
      break
    case 'mainNav':
      data[key] = []
      for (let item of value) {
        for (let [itemKey, itemValue] of Object.entries(item.fields)) {
          switch (itemKey) {
          case 'entryTitle':
            item[itemKey] = item['entryTitle'] = itemValue;
            break
          case 'title':
            item[itemKey] = item['title'] = itemValue;
            break
          case 'image':
            item[itemKey] = item['image'] = getImgSrc(itemValue);
            break
          case 'navItems':
            item[itemKey] = [];
            for (let navItem of itemValue) {
              const navItemEntries = Object.entries(navItem.fields || {})
              for (let [navItemKey, navItemValue] of navItemEntries) {
                switch (navItemKey) {
                case 'description':
                  navItem[navItemKey] = navItem['description'] = navItemValue;
                  break
                case 'entryTitle':
                  navItem[navItemKey] = navItem['entryTitle'] = navItemValue;
                  break
                case 'featured':
                  navItem[navItemKey] = navItem['featured'] = navItemValue;
                  break
                case 'new':
                  navItem[navItemKey] = navItem['new'] = navItemValue;
                  break
                case 'image':
                  navItem[navItemKey] = navItem['image'] = getImgSrc(navItemValue)
                  break
                case 'linkText':
                  navItem[navItemKey] = navItem['linkText'] = navItemValue;
                  break
                case 'linkUrl':
                  navItem[navItemKey] = navItem['linkUrl'] = navItemValue;
                  break
                case 'subtitle':
                  navItem[navItemKey] = navItem['subtitle'] = navItemValue;
                  break
                case 'title':
                  navItem[navItemKey] = navItem['title'] = navItemValue;
                  break
                default:
                  navItem[navItemKey] = navItemValue;
                }
              }
              item[itemKey].push(navItem);
            }
            break
          case 'secondaryNavItems':
            item[itemKey] = [];
            for (let secondaryNavItem of itemValue) {
              for (let [secondaryNavItemKey, secondaryNavItemValue] of Object.entries(secondaryNavItem.fields)) {
                switch (secondaryNavItemKey) {
                case 'entryTitle':
                  secondaryNavItem[secondaryNavItemKey] = secondaryNavItem['entryTitle'] = secondaryNavItemValue;
                  break
                case 'featured':
                  secondaryNavItem[secondaryNavItemKey] = secondaryNavItem['featured'] = secondaryNavItemValue;
                  break
                case 'image':
                  secondaryNavItem[secondaryNavItemKey] = secondaryNavItem['image'] = getImgSrc(secondaryNavItemValue)
                  break
                case 'linkText':
                  secondaryNavItem[secondaryNavItemKey] = secondaryNavItem['linkText'] = secondaryNavItemValue;
                  break
                case 'linkUrl':
                  secondaryNavItem[secondaryNavItemKey] = secondaryNavItem['linkUrl'] = secondaryNavItemValue;
                  break
                case 'externalLink':
                  secondaryNavItem[secondaryNavItemKey] = secondaryNavItem['externalLink'] = secondaryNavItemValue;
                  break
                default:
                  secondaryNavItem[secondaryNavItemKey] = secondaryNavItemValue;
                }
              }
              item[itemKey].push(secondaryNavItem);
            }
            break
          default:
            item[itemKey] = itemValue;
          }
        }
        data[key].push(item);
      }
      break
    case 'staticNav':
      data[key] = {}
      for (let [fieldKey, fieldValue] of Object.entries(value.fields)) {
        switch (fieldKey) {
        case 'entryTitle':
          data[key][fieldKey] = data[key]['entryTitle'] = fieldValue;
          break
        case 'title':
          data[key][fieldKey] = data[key]['title'] = fieldValue;
          break
        case 'navItems':
          data[key][fieldKey] = [];
          for (let navItem of fieldValue) {
            for (let [navItemKey, navItemValue] of Object.entries(navItem.fields)) {
              switch (navItemKey) {
              case 'description':
                navItem[navItemKey] = navItem['description'] = navItemValue;
                break
              case 'entryTitle':
                navItem[navItemKey] = navItem['entryTitle'] = navItemValue;
                break
              case 'featured':
                navItem[navItemKey] = navItem['featured'] = navItemValue;
                break
              case 'new':
                navItem[navItemKey] = navItem['new'] = navItemValue;
                break
              case 'image':
                navItem[navItemKey] = navItem['image'] = getImgSrc(navItemValue)
                break
              case 'linkText':
                navItem[navItemKey] = navItem['linkText'] = navItemValue;
                break
              case 'linkUrl':
                navItem[navItemKey] = navItem['linkUrl'] = navItemValue;
                break
              case 'subtitle':
                navItem[navItemKey] = navItem['subtitle'] = navItemValue;
                break
              case 'title':
                navItem[navItemKey] = navItem['title'] = navItemValue;
                break
              default:
                navItem[navItemKey] = navItemValue;
              }
            }
            data[key][fieldKey].push(navItem);
          }
          break
        default:
          data[key][fieldKey] = fieldValue;
        }
      }
      break
    case 'accountNav':
      data[key] = {}
      for (let [fieldKey, fieldValue] of Object.entries(value.fields)) {
        switch (fieldKey) {
        case 'entryTitle':
          data[key][fieldKey] = data[key]['entryTitle'] = fieldValue;
          break
        case 'title':
          data[key][fieldKey] = data[key]['title'] = fieldValue;
          break
        case 'navItems':
          data[key][fieldKey] = [];
          for (let navItem of fieldValue) {
            for (let [navItemKey, navItemValue] of Object.entries(navItem.fields)) {
              switch (navItemKey) {
              case 'description':
                navItem[navItemKey] = navItem['description'] = navItemValue;
                break
              case 'entryTitle':
                navItem[navItemKey] = navItem['entryTitle'] = navItemValue;
                break
              case 'featured':
                navItem[navItemKey] = navItem['featured'] = navItemValue;
                break
              case 'new':
                navItem[navItemKey] = navItem['new'] = navItemValue;
                break
              case 'image':
                navItem[navItemKey] = navItem['image'] = getImgSrc(navItemValue)
                break
              case 'linkText':
                navItem[navItemKey] = navItem['linkText'] = navItemValue;
                break
              case 'linkUrl':
                navItem[navItemKey] = navItem['linkUrl'] = navItemValue;
                break
              case 'subtitle':
                navItem[navItemKey] = navItem['subtitle'] = navItemValue;
                break
              case 'title':
                navItem[navItemKey] = navItem['title'] = navItemValue;
                break
              default:
                navItem[navItemKey] = navItemValue;
              }
            }
            data[key][fieldKey].push(navItem);
          }
          break
        default:
          data[key][fieldKey] = fieldValue;
        }
      }
      break
    case 'footerNav':
      data[key] = {}
      for (let [fieldKey, fieldValue] of Object.entries(value.fields)) {
        switch (fieldKey) {
        case 'entryTitle':
          data[key][fieldKey] = data[key]['entryTitle'] = fieldValue;
          break
        case 'title':
          data[key][fieldKey] = data[key]['title'] = fieldValue;
          break
        case 'navItems':
          data[key][fieldKey] = [];
          for (let navItem of fieldValue) {
            for (let [navItemKey, navItemValue] of Object.entries(navItem.fields)) {
              switch (navItemKey) {
              case 'description':
                navItem[navItemKey] = navItem['description'] = navItemValue;
                break
              case 'entryTitle':
                navItem[navItemKey] = navItem['entryTitle'] = navItemValue;
                break
              case 'featured':
                navItem[navItemKey] = navItem['featured'] = navItemValue;
                break
              case 'new':
                navItem[navItemKey] = navItem['new'] = navItemValue;
                break
              case 'image':
                navItem[navItemKey] = navItem['image'] = getImgSrc(navItemValue)
                break
              case 'linkText':
                navItem[navItemKey] = navItem['linkText'] = navItemValue;
                break
              case 'linkUrl':
                navItem[navItemKey] = navItem['linkUrl'] = navItemValue;
                break
              case 'subtitle':
                navItem[navItemKey] = navItem['subtitle'] = navItemValue;
                break
              case 'title':
                navItem[navItemKey] = navItem['title'] = navItemValue;
                break
              default:
                navItem[navItemKey] = navItemValue;
              }
            }
            data[key][fieldKey].push(navItem);
          }
          break
        default:
          data[key][fieldKey] = fieldValue;
        }
      }
      break
    case 'questions':
      data[key] = []
      for (let item of value) {
        for (let [itemKey, itemValue] of Object.entries(item.fields)) {
          switch (itemKey) {
          case 'entryTitle':
            item[itemKey] = item['entryTitle'] = itemValue;
            break
          case 'handle':
            item[itemKey] = item['handle'] = itemValue;
            break
          case 'multipleAnswers':
            item[itemKey] = item['multipleAnswers'] = itemValue;
            break
          case 'points':
            item[itemKey] = item['points'] = itemValue;
            break
          case 'subtitle':
            item[itemKey] = item['subtitle'] = itemValue;
            break
          case 'title':
            item[itemKey] = item['title'] = itemValue;
            break
          case 'answers':
            item[itemKey] = [];
            for (let answerItem of itemValue) {
              for (let [answerItemKey, answerItemValue] of Object.entries(answerItem.fields)) {
                switch (answerItemKey) {
                case 'collections':
                  answerItem[answerItemKey] = answerItem['collections'] = answerItemValue;
                  break
                case 'displayAlts':
                  answerItem[answerItemKey] = answerItem['displayAlts'] = answerItemValue;
                  break
                case 'entryTitle':
                  answerItem[answerItemKey] = answerItem['entryTitle'] = answerItemValue;
                  break
                case 'image':
                  answerItem[answerItemKey] = answerItem['image'] = getImgSrc(answerItemValue)
                  break
                case 'altImage':
                  answerItem[answerItemKey] = answerItem['altImage'] = getImgSrc(answerItemValue)
                  break
                case 'title':
                  answerItem[answerItemKey] = answerItem['title'] = answerItemValue;
                  break
                default:
                  answerItem[answerItemKey] = answerItemValue;
                }
              }
              item[itemKey].push(answerItem);
            }
            break
          default:
            item[itemKey] = itemValue;
          }
        }
        data[key].push(item);
      }
      break
    case 'card':
      data[key] = {}
      for (let [fieldKey, fieldValue] of Object.entries(value.fields)) {
        switch (fieldKey) {
        case 'image':
          data[key][fieldKey] = getImgSrc(fieldValue)
          break
        case 'mobileImage':
          data[key][fieldKey] = getImgSrc(fieldValue)
          break
        case 'video':
          data[key][fieldKey] = getVideoSrc(fieldValue)
          break
        case 'showReviewCard':
          data[key][fieldKey] = data[key]['showReviewCard'] = fieldValue
          break
        case 'description':
          data[key][fieldKey] = data[key]['description'] = contentToHtml(fieldValue, {
            contentToHtmlFn: vm.contentToHtmlFn
          })
          break
        case 'belowButtonText':
          data[key][fieldKey] = data[key]['belowButtonText'] = contentToHtml(fieldValue, {
            contentToHtmlFn: vm.contentToHtmlFn
          })
          break
        case 'title':
          data[key][fieldKey] = data[key]['title'] = fieldValue
          break
        case 'backgroundImage':
          data[key][fieldKey] = getImgSrc(fieldValue)
          break
        case 'cta':
        case 'mobileCta':
          data[key][fieldKey] = {}
          if (fieldValue.fields) {
            for (let [ctaKey, ctaValue] of Object.entries(fieldValue.fields)) {
              switch (ctaKey) {
              case 'buttonSize':
                data[key][fieldKey][ctaKey] = data[key][fieldKey]['buttonSize'] = ctaValue
                break
              case 'entryTitle':
                data[key][fieldKey][ctaKey] = data[key][fieldKey]['entryTitle'] = ctaValue
                break
              case 'text':
                data[key][fieldKey][ctaKey] = data[key][fieldKey]['text'] = ctaValue
                break
              case 'type':
                data[key][fieldKey][ctaKey] = data[key][fieldKey]['type'] = ctaValue
                break
              case 'url':
                data[key][fieldKey][ctaKey] = data[key][fieldKey]['url'] = ctaValue
                break
              default:
                data[key][fieldKey][ctaKey] = ctaValue
              }
            }
          }
          break
        default:
          data[key][fieldKey] = fieldValue
        }
      }
      break
    case 'sections':
      data[key] = []
      for (let item of value) {
        const itemFields = (item && item.fields) ? item.fields : (item || {})
        for (let [itemKey, itemValue] of Object.entries(itemFields)) {
          switch (itemKey) {
          case 'entryTitle':
            item[itemKey] = item['entryTitle'] = itemValue;
            break
          case 'title':
            item[itemKey] = item['title'] = itemValue;
            break
          case 'anchor':
            item[itemKey] = item['anchor'] = contentToHtml(itemValue, {
              contentToHtmlFn: vm.contentToHtmlFn
            });
            break
          case 'description':
            item[itemKey] = item['description'] = contentToHtml(itemValue, {
              contentToHtmlFn: vm.contentToHtmlFn
            });
            break
          case 'image':
            item[itemKey] = getImgSrc(itemValue);
            break
          case 'mobileImage':
            item[itemKey] = getImgSrc(itemValue);
            break
          case 'mobileBackgroundImage':
            item[itemKey] = getImgSrc(itemValue);
            break
          case 'desktopBackgroundImage':
            item[itemKey] = getImgSrc(itemValue);
            break
          case 'cta':
            item[itemKey] = {}
            if (itemValue.fields) {
              for (let [ctaKey, ctaValue] of Object.entries(itemValue.fields)) {
                switch (ctaKey) {
                case 'buttonSize':
                  item[itemKey][ctaKey] = item[itemKey]['buttonSize'] = ctaValue
                  break
                case 'entryTitle':
                  item[itemKey][ctaKey] = item[itemKey]['entryTitle'] = ctaValue
                  break
                case 'text':
                  item[itemKey][ctaKey] = item[itemKey]['text'] = ctaValue
                  break
                case 'type':
                  item[itemKey][ctaKey] = item[itemKey]['type'] = ctaValue
                  break
                case 'url':
                  item[itemKey][ctaKey] = item[itemKey]['url'] = ctaValue
                  break
                default:
                  item[itemKey][ctaKey] = ctaValue
                }
              }
            }
            break
          case 'imagePosition':
            item[itemKey] = item['imagePosition'] = itemValue;
            break
          default:
            item[itemKey] = itemValue;
          }
        }
        data[key].push(item);
      }
      break
    case 'cta':
    case 'mobileCta':
      data[key] = {}
      for (let [fieldKey, fieldValue] of Object.entries(value.fields)) {
        switch (fieldKey) {
        case 'buttonSize':
          data[key][fieldKey] = data[key]['buttonSize'] = fieldValue
          break
        case 'entryTitle':
          data[key][fieldKey] = data[key]['entryTitle'] = fieldValue
          break
        case 'text':
          data[key][fieldKey] = data[key]['text'] = fieldValue
          break
        case 'type':
          data[key][fieldKey] = data[key]['type'] = fieldValue
          break
        case 'url':
          data[key][fieldKey] = data[key]['url'] = fieldValue
          break
        default:
          data[key][fieldKey] = fieldValue
        }
      }
      break
    case 'retakeCta':
      data[key] = {}
      for (let [fieldKey, fieldValue] of Object.entries(value.fields)) {
        switch (fieldKey) {
        case 'buttonSize':
          data[key][fieldKey] = data[key]['buttonSize'] = fieldValue
          break
        case 'entryTitle':
          data[key][fieldKey] = data[key]['entryTitle'] = fieldValue
          break
        case 'text':
          data[key][fieldKey] = data[key]['text'] = fieldValue
          break
        case 'type':
          data[key][fieldKey] = data[key]['type'] = fieldValue
          break
        case 'url':
          data[key][fieldKey] = data[key]['url'] = fieldValue
          break
        default:
          data[key][fieldKey] = fieldValue
        }
      }
      break
    case 'favoritesCta':
      data[key] = {}
      for (let [fieldKey, fieldValue] of Object.entries(value.fields)) {
        switch (fieldKey) {
        case 'buttonSize':
          data[key][fieldKey] = data[key]['buttonSize'] = fieldValue
          break
        case 'entryTitle':
          data[key][fieldKey] = data[key]['entryTitle'] = fieldValue
          break
        case 'text':
          data[key][fieldKey] = data[key]['text'] = fieldValue
          break
        case 'type':
          data[key][fieldKey] = data[key]['type'] = fieldValue
          break
        case 'url':
          data[key][fieldKey] = data[key]['url'] = fieldValue
          break
        default:
          data[key][fieldKey] = fieldValue
        }
      }
      break
    case 'findYourRing':
      data[key] = {}
      for (let [fieldKey, fieldValue] of Object.entries(value.fields)) {
        switch (fieldKey) {
        case 'entryTitle':
          data[key][fieldKey] = data[key]['entryTitle'] = fieldValue
          break
        case 'featured':
          data[key][fieldKey] = data[key]['featured'] = fieldValue
          break
        case 'image':
          data[key][fieldKey] = data[key]['text'] = getImgSrc(fieldValue)
          break
        case 'linkText':
          data[key][fieldKey] = data[key]['linkText'] = fieldValue
          break
        case 'linkUrl':
          data[key][fieldKey] = data[key]['linkUrl'] = fieldValue
          break
        case 'title':
          data[key][fieldKey] = data[key]['title'] = fieldValue
          break
        default:
          data[key][fieldKey] = fieldValue
        }
      }
      break
    case 'personalizationStep':
      data[key] = [];
      for (let step of value) {
        for (let [stepKey, stepValue] of Object.entries(step.fields)) {
          switch (stepKey) {
          case 'personalizationDescription':
            step[stepKey] = step['personalizationDescription'] = contentToHtml(stepValue, {
              contentToHtmlFn: vm.contentToHtmlFn
            })
            break
          default:
            step[stepKey] = stepValue
          }
        }
        data[key].push(step);
      }
      break
    case 'slides':
      data[key] = value.map(({
        fields: { name, quotation: quote, featuredMedia }
      }) => ({
        name,
        quote,
        imageUrl: getImgSrc(featuredMedia)
      }))
      break
    case 'children':
      data.slides = value.map(({
        data: { title: name, html: quote, image }
      }) => ({
        name, quote, imageUrl: getImgSrc(image)
      }))
      break
    case 'description':
      data[key] = contentToHtml(value, {
        contentToHtmlFn: vm.contentToHtmlFn
      })
      break
    case 'engravingParagraph':
      data[key] = contentToHtml(value, {
        contentToHtmlFn: vm.contentToHtmlFn
      })
      break
    case 'globalNotification':
      data[key] = contentToHtml(value, {
        contentToHtmlFn: vm.contentToHtmlFn
      })
      break
    case 'tab':
    case 'tabs':
      data[key] = [];
      for(let card of value){
        data[key].push(mapSection(vm, card))
      }
      break
    case 'footerImages':
      data[key] = []
      for (let image of value) {
        data[key].push(getImgSrc(image))
      }
      break
    case 'body':
      data[key] = contentToHtml(value, {
        contentToHtmlFn: vm.contentToHtmlFn
      })
      break
    case 'pageContent1':
      data.topContent = mapSections(vm, value)
      break
    case 'pageContentAfter':
      data.bottomContent = mapSections(vm, value)
      break
    case 'disclaimer':
      data[key] = contentToHtml(value, {
        contentToHtmlFn: vm.contentToHtmlFn
      })
      break
    }
  }
}

/**
 * the original nacelle strategy requires all content item fields to be
 * globally unique OR have identical mappings if they exist on multiple types.
 *
 * type-specific field mapping defers to a function for each content type
 * removing the need for globally unique field names. mappers can be
 * registered below as needed.
 */
const mapByType = {
  socialMediaGallery: (vm, section, data) => {
    for (let [key, value] of Object.entries(section.fields)) {
      data[key] = value
    }
  },
  NacelleReference: (vm, section) => {
    if (process.env.FEAT_THROW_NACELLE_REFERENCE) {
      console.warn(
        'NacelleReference Error',
        'if a piece of content is looking for a reference that is not in Nacelle’s index, it generates a reference error. It could be that a webhook didn’t fire for the referenced entry, it could be that the referenced entry was deleted but the top level entry still refers to it.',
        'set FEAT_THROW_NACELLE_REFERENCE to error out when this issue is detected.',
        'https://support.nacelle.com/hc/en-us/articles/10302991939355-Fixing-Nacelle-Reference-Errors',
        section
      )
      throw new Error(`${section.type}: ${section.message}`)
    }
  },
  fallbackFieldCase: mapByFieldCase
}

export const mapSection = (vm, section) => {
  const id = get(section, 'id') || get(section, 'sys.id') || uniqueId()
  const handle = get(section, 'fields.handle') || ''
  if (!section) {
    return {
      id: `handle-${handle}--id-${id}`,
      handle,
      contentType: null,
      data: {}
    }
  }
  const contentType = getContentType(section)
  let data = {} // mutated by data mapper
  const mapper = (mapByType[contentType])
    ? mapByType[contentType]
    : mapByType.fallbackFieldCase
  mapper(vm, section, data)
  if (contentType.toLowerCase().includes('productgrid')) {
    data.products = vm.products
  }
  return {
    id: `handle-${handle}--id-${id}`,
    handle,
    contentType,
    data
  }
}

class MappingError extends Error {
  constructor({ section, err }) {
    super('MappingError', ...slice(arguments, 1))
    if (Error.captureStackTrace) {
      if (err) {
        Error.captureStackTrace(this, err)
      } else {
        Error.captureStackTrace(this, MappingError)
      }
    }
    this.section = JSON.stringify(section, null, '  ')
    this.message = `[MappingError: ${err.message}] unable to map: ${this.section}`
  }
}

/**
 * loop over the sections and return the result
 * could be lazy, but alas there is still at least one function call per item
 */
export const mapSections = (vm, sections) => {
  let result = []
  for (let section of sections) {
    try {
      if (section && section.fields) {
        result.push(mapSection(vm, section))
      } else if (section) {
        result.push(section)
      }
    } catch (err) {
      const mapErr = new MappingError({ section, err })
      if (process.env.NODE_ENV === 'development') {
        console.error(err, mapErr)
      } else {
        throw mapErr
      }
    }
  }
  return result
}

export const mapContent = (page, options = {}) => {
  const pageSectionsCount = get(page, 'sections.length')
  const ctx = {
    products: options.products,
    contentToHtmlFn: options.contentToHtmlFn,
  }
  let result = {}
  if (pageSectionsCount && pageSectionsCount > 0) {
    result.sections = []
    for (let section of mapSections(ctx, page.sections)) {
      // omit drafts and other missing reference objects
      if (section.contentType !== 'NacelleReference') {
        result.sections.push(section)
      }
    }
  }
  if (page && page.fields && Object.keys(page.fields).length > 0) {
    const mapped = mapSection(ctx, page)
    for (let [ key, value ] of Object.entries(mapped.data)) {
      result[key] = value
    }
  }
  if (page && page.type === 'blog') {
    result.handle = page.handle
    result.articleLists = page.articleLists
  }
  if (page && page.blogHandle) {
    result.featuredMedia = page.featuredMedia
    result.content = get(page, 'fields.content.content') ? contentToHtml(page.fields.content.content, {
      contentToHtmlFn: ctx.contentToHtmlFn
    }) : null
    result.title = get(page, 'title')
    result.excerpt = get(page, 'excerpt')
    result.handle = page.handle
  }
  if (process.env.NODE_ENV !== 'production') {
    result._rawContentful = page
  }
  return result
}
